/**
 * Component for handling nested province navigation in the header menu.
 * Provides hover-based navigation through provinces and their related pages with smooth UX.
 */

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { generateProvincePageUrl } from "@/lib/province-url-helpers";

const ProvinceMenuSection = ({ 
  item, 
  provinces, 
  onProvinceSubMenuChange,
}: { 
  item: any; 
  provinces: any[];
  onProvinceSubMenuChange?: (open: boolean) => void;
}) => {
  const [hoveredProvince, setHoveredProvince] = useState<number | null>(null);
  const [showProvinces, setShowProvinces] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const provinceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setShowProvinces(true);
  };

  const handleMouseLeave = () => {
    // Add a delay before closing to allow smooth navigation
    closeTimeoutRef.current = setTimeout(() => {
      setShowProvinces(false);
    }, 600); // The time it takes for the province menu to close if not hovering
  };

  const clearProvinceTimeout = () => {
    if (provinceTimeoutRef.current) {
      clearTimeout(provinceTimeoutRef.current);
      provinceTimeoutRef.current = null;
    }
  };

  const setProvinceTimeout = (provinceId: number | null) => {
    clearProvinceTimeout();
    provinceTimeoutRef.current = setTimeout(() => {
      setHoveredProvince(null);
    }, 600);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (provinceTimeoutRef.current) {
        clearTimeout(provinceTimeoutRef.current);
      }
    };
  }, []);

  // Notify parent when province submenu state changes
  useEffect(() => {
    onProvinceSubMenuChange?.(showProvinces);
  }, [showProvinces, onProvinceSubMenuChange]);

  const top = "-1.5rem";

  const provinceMenuStyle = {
    top,
    backgroundImage: "linear-gradient(to bottom, #4b5563 0%, #4b5563 6px, transparent 6px, transparent 12px)",
    backgroundSize: "1px 12px",
    backgroundRepeat: "repeat-y",
    backgroundPosition: "0 0",
  };

  const provincePagesMenuStyle = {
    top,
    marginLeft: "calc(140px + 1rem + 1rem)",
    backgroundImage: "linear-gradient(to bottom, #4b5563 0%, #4b5563 6px, transparent 6px, transparent 12px)",
    backgroundSize: "1px 12px",
    backgroundRepeat: "repeat-y",
    backgroundPosition: "0 0",
  };

  return (
    <div className="relative">
      <div
        className="flex min-w-[140px] cursor-pointer items-center font-oswald text-base font-bold uppercase text-white"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Provincies text */}
        {item.title}
        {provinces && provinces.length > 0 && showProvinces && (
          <div className="ml-2 h-2 w-2 rounded-full bg-nsc-yellow" />
        )}
      </div>

      {/* Invisible bridge to prevent menu from closing */}
      {showProvinces && (
        <div
          className="absolute left-full top-0 h-full w-4"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}

      {/* Provinces Menu - appears on hover to the right */}
      {showProvinces && provinces && provinces.length > 0 && (
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-full ml-4 min-h-[500px] min-w-[140px] bg-nsc-dark-blue pl-4"
            style={provinceMenuStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <ul className="flex flex-col gap-4">
              {provinces.map((province: any) => (
                <ProvinceMenuItem
                  key={province.id}
                  province={province}
                  hoveredProvince={hoveredProvince}
                  setHoveredProvince={setHoveredProvince}
                  clearProvinceTimeout={clearProvinceTimeout}
                  setProvinceTimeout={setProvinceTimeout}
                />
              ))}
            </ul>
          </motion.div>

          {/* Enhanced invisible bridge for province pages submenu - covers the entire path */}
          {hoveredProvince && provinces.find((p) => p.id === hoveredProvince)?.nsc_provinciepaginas?.length > 0 && (
            <div
              className="absolute left-full"
              style={provincePagesMenuStyle}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
          )}

          {/* Province Pages Submenu - rendered at container level for consistent positioning */}
          {hoveredProvince && provinces.find((p) => p.id === hoveredProvince)?.nsc_provinciepaginas?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="absolute left-full min-h-[500px] min-w-[200px] bg-nsc-dark-blue pl-4"
              style={provincePagesMenuStyle}
              onMouseEnter={() => {
                handleMouseEnter();
                clearProvinceTimeout(); // Keep the current province active
              }}
              onMouseLeave={() => {
                handleMouseLeave();
                setProvinceTimeout(hoveredProvince); // Set timeout to clear province`
              }}
            >
              <ul className="flex flex-col gap-4">
                {provinces
                  .find((p) => p.id === hoveredProvince)
                  ?.nsc_provinciepaginas.map((page: any) => (
                    <li key={page.id} className="font-oswald text-base font-bold uppercase text-white">
                      <Link
                        href={generateProvincePageUrl(provinces.find((p) => p.id === hoveredProvince)?.slug, page.slug)}
                        className="block transition-colors hover:text-gray-300"
                      >
                        {page.name}
                      </Link>
                    </li>
                  ))}
              </ul>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
};

const ProvinceMenuItem = ({
  province,
  hoveredProvince,
  setHoveredProvince,
  clearProvinceTimeout,
  setProvinceTimeout,
}: {
  province: any;
  hoveredProvince: number | null;
  setHoveredProvince: (id: number | null) => void;
  clearProvinceTimeout: () => void;
  setProvinceTimeout: (provinceId: number | null) => void;
}) => {
  const handleMouseEnter = () => {
    clearProvinceTimeout();
    setHoveredProvince(province.id);
  };

  const handleMouseLeave = () => {
    setProvinceTimeout(province.id);
  };

  return (
    <li className="relative font-oswald text-base font-bold uppercase text-white">
      {/* Province home page link - uses clean URL when page slug matches province slug */}
      <Link
        href={generateProvincePageUrl(province.slug, province.slug)}
        className="inline-flex cursor-pointer items-center transition-colors hover:text-gray-300"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {province.title}
        {province.nsc_provinciepaginas &&
          province.nsc_provinciepaginas.length > 0 &&
          hoveredProvince === province.id && <div className="ml-2 h-2 w-2 rounded-full bg-nsc-yellow" />}
      </Link>
    </li>
  );
};

export default ProvinceMenuSection;
